import { useState, useRef, useEffect, useCallback } from "react";
import { Save, Trash2, Edit, Plus, X, AlertCircle, Code, Loader2, Eye, TrendingUp, Sparkles, Settings } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "../hooks/useToast";
import { useAuth } from "../components/features/AuthContext";
import { apiRequest } from "../components/lib/queryClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Editor, { useMonaco } from "@monaco-editor/react";
import z from "zod";
import { ApiResponse } from "../components/lib/utils";
import { mergeVariableSuggestions } from "../components/lib/indicatorVariables";
import { CustomModelAddStocks } from "../components/CustomAddStocksModal";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";
import { Separator } from "../components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import React from "react";
import { Button } from "../components/ui/button";
import { extractVariablesFromFunctionCode } from "./filter-management";

// ========================= SCHEMAS =========================
export const StockObjectSchema = z.object({
  _id: z.string().optional(),
  security_id: z.number().int(),
  underlying_symbol: z.string(),
  symbol_name: z.string(),
  instrument_type: z.string(),
  instrument: z.string(),
  exchange_segment: z.string(),
  source: z.string().default("dhan"),
});
export const cryptoPairSchema = z.object({
  symbol: z.string().min(1, "Pair is required"),
  _id: z.string().optional(),
  category: z.enum(["crypto"]),
})
export type StockObject = z.infer<typeof StockObjectSchema>;
export type CryptoPair = z.infer<typeof cryptoPairSchema>;

const FilterApiSchema = z.object({
  id: z.string().optional(),
  function_code: z.string(),
  variables: z.array(z.string()),
  function_name: z.string(),
  category: z.enum(["stocks", "crypto"]),
  type: z.enum(["BUY", "SELL", "EXIT", "LEADER"]),
  isActive: z.boolean(),
});
export type FilterApiSchema = z.infer<typeof FilterApiSchema>;
// ============================================================

export default function LeaderManagement() {
  const { currentTradingMode, user } = useAuth();
  const category = currentTradingMode;
  const queryClient = useQueryClient();
  const [pairModalOpen, setPairModalOpen] = useState<boolean>(false);
  const [expandedFilterIds, setExpandedFilterIds] = useState<Set<string>>(new Set());
  const [selectedPairs, setSelectedPairs] = useState<(CryptoPair | StockObject)[]>([]);
  const [editingStrategy, setEditingStrategy] = useState<FilterApiSchema | null>(null);
  const [pairSearch, setPairSearch] = useState<string>("");
  console.log("selected_pairs", selectedPairs);

  // Monaco & variables
  const editorRef = useRef<any>(null);
  const monaco = useMonaco();
  const [codeInput, setCodeInput] = useState<string>(`def leader_strategy(data):
    """
    Leader Strategy Function
    Implement your leader selection logic here
    """
    # Example: Return True for leaders meeting criteria
    if data.get('volume', 0) > 100000:
        return True
    return False`);
  const [variableSuggestions, setVariableSuggestions] = useState<string[]>([]);
  const strategyType = "LEADER";
  const [isActive, setIsActive] = useState<boolean>(true);
  const [strategyName, setStrategyName] = useState<string>("");

  // ==================== FETCH LEADERS ====================
  const {
    data: leaders,
    isLoading: isLoadingLeaders,
    error: leadersError,
    refetch: refetchLeaders,
  } = useQuery<StockObject[] | CryptoPair[]>({
    queryKey: ["api/leaders", category],
    queryFn: async () => {
      const params = new URLSearchParams({ category });
      const res = await apiRequest<ApiResponse<StockObject[] | CryptoPair[]>>(
        "GET",
        `api/leaders?${params}`
      );
      return res.data;
    },
    enabled: !!category,
  });

  // ==================== CREATE LEADER ====================
  const { mutate: createLeader, isPending: isCreatingLeader } = useMutation({
    mutationFn: async (payload: (StockObject | CryptoPair)[]) => {
      const params = {
        category
      };
      return apiRequest<ApiResponse<null>>("POST", `api/leaders`, payload, params);
    },

    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["api/leaders", category] });

      toast({
        title: "Success",
        description: res.message || "Leader created successfully",
        variant: "default",
      });

      setPairModalOpen(false);
      setSelectedPairs([]);
    },

    onError: (error: Error) => {
      toast({
        title: "Error creating leader",
        description: error.message || "Failed to create leader",
        variant: "destructive",
      });
    },
  });

  // ==================== DELETE LEADER ====================
  const { mutate: deleteLeader, isPending: isDeletingLeader } = useMutation({
    mutationFn: async (id: string | number) =>
      apiRequest<ApiResponse<null>>("DELETE", `api/leaders/${id}`),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["api/leaders", category] });
      toast({
        title: "Deleted",
        description: res.message || "Leader removed successfully",
        variant: "default",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting leader",
        description: error.message || "Failed to delete leader",
        variant: "destructive",
      });
    },
  });

  // ==================== FETCH STRATEGIES ====================
  const {
    data: strategies,
    isLoading: isLoadingStrategies,
    error: strategiesError,
  } = useQuery<FilterApiSchema[]>({
    queryKey: ["api/strategies", category],
    queryFn: async () => {
      const params = new URLSearchParams({ category, type: "LEADER" });
      const res = await apiRequest<ApiResponse<FilterApiSchema[]>>(
        "GET",
        `api/strategies?${params}`
      );
      return res.data;
    },
    enabled: !!category,
  });

  type CreateStrategyPayload = Omit<FilterApiSchema, "id">;
  type UpdateStrategyPayload = Partial<FilterApiSchema> & { id: string };

  // ==================== CREATE STRATEGY ====================
  const { mutate: createStrategy, isPending: isCreatingStrategy } = useMutation({
    mutationFn: async (payload: CreateStrategyPayload) =>
      apiRequest<ApiResponse<null>>("POST", "api/strategies", payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["api/strategies", category] });
      toast({
        title: "Success",
        description: res.message || "Strategy created successfully",
        variant: "default",
      });
      resetEditor();
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating strategy",
        description: error.message || "Failed to create strategy",
        variant: "destructive",
      });
    },
  });

  // ==================== UPDATE STRATEGY ====================
  const { mutate: updateStrategy, isPending: isUpdatingStrategy } = useMutation({
    mutationFn: async ({ id, ...payload }: UpdateStrategyPayload) =>
      apiRequest<ApiResponse<null>>("PUT", `api/strategies/${id}`, payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["api/strategies", category] });
      toast({
        title: "Updated",
        description: res.message || "Strategy updated successfully",
        variant: "default",
      });
      resetEditor();
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating strategy",
        description: error.message || "Failed to update strategy",
        variant: "destructive",
      });
    },
  });

  // ==================== DELETE STRATEGY ====================
  const { mutate: deleteStrategy, isPending: isDeletingStrategy } = useMutation({
    mutationFn: async (id: string) =>
      apiRequest<ApiResponse<null>>("DELETE", `api/strategies/${id}`),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["api/strategies", category] });
      toast({
        title: "Removed",
        description: res.message || "Strategy deleted successfully",
        variant: "default",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting strategy",
        description: error.message || "Failed to delete strategy",
        variant: "destructive",
      });
    },
  });

  // ==================== VARIABLES AUTOCOMPLETE ====================
  const { data: variableData } = useQuery<string[]>({
    queryKey: ["filter-variables"],
    queryFn: async () => {
      const res = await apiRequest<ApiResponse<{ variables: string[] }>>(
        "GET",
        "api/get-filter-variables"
      );
      return res.data.variables || [];
    },
  });

  useEffect(() => {
    if (variableData) {
      // Leader strategies run on the crypto backend, which also calculates
      // SMA / RSI / Bollinger variables that get-filter-variables does not list.
      setVariableSuggestions(
        category === "crypto" ? mergeVariableSuggestions(variableData) : variableData
      );
    }
  }, [variableData, category]);

  useEffect(() => {
    if (!monaco || variableSuggestions.length === 0) return;

    const provider = monaco.languages.registerCompletionItemProvider("python", {
      triggerCharacters: [".", " "],
      provideCompletionItems: (model, position) => {
        const lineUntil = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });

        const match = lineUntil.match(/\b([a-zA-Z_][a-zA-Z0-9_]*)$/);
        if (!match) return null;

        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        const suggestions = variableSuggestions.map((variable) => ({
          label: variable,
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: variable,
          range,
          detail: "Suggested variable",
          documentation: `Available data variable: ${variable}`,
        }));

        return { suggestions };
      },
    });

    return () => provider.dispose();
  }, [monaco, variableSuggestions]);

  // ==================== HELPER FUNCTIONS ====================
  const resetEditor = useCallback(() => {
    setCodeInput(`def leader_strategy(data):
    """
    Leader Strategy Function
    Implement your leader selection logic here
    """
    # Example: Return True for leaders meeting criteria
    if data.get('volume', 0) > 100000:
        return True
    return False`);
    setStrategyName("");
    setIsActive(true);
    setEditingStrategy(null);
  }, []);

  

  // ==================== STRATEGY ACTION HANDLERS ====================
  const handleAddStrategy = useCallback(() => {
    if (!codeInput.trim()) {
      toast({
        title: "Error",
        description: "Strategy code cannot be empty",
        variant: "destructive",
      });
      return;
    }

    const function_name = codeInput.match(/def\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/)?.[1] || "unknown_func";
    const variables = extractVariablesFromFunctionCode(codeInput, function_name);

    createStrategy({
      function_code: codeInput,
      variables,
      function_name,
      type: strategyType,
      isActive,
      category,
    });
  }, [codeInput, strategyName, variableSuggestions, isActive, category, createStrategy]);

  const handleEditStrategy = useCallback((strategy: FilterApiSchema) => {
    setEditingStrategy(strategy);
    setCodeInput(strategy.function_code);
    setStrategyName(strategy.function_name);
    setIsActive(strategy.isActive);
    document.getElementById('strategy-editor')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleUpdateStrategy = useCallback(() => {
    if (!editingStrategy?.id) {
      toast({
        title: "Error",
        description: "No strategy selected for editing",
        variant: "destructive",
      });
      return;
    }
    const function_name = codeInput.match(/def\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/)?.[1] || "unknown_func";
    const variables = extractVariablesFromFunctionCode(codeInput, function_name);

    updateStrategy({
      id: editingStrategy.id,
      function_code: codeInput,
      function_name:function_name,
      variables: variables,
      type: strategyType,
      category,
      isActive,
    });
  }, [editingStrategy, codeInput, strategyName, isActive, updateStrategy]);

  const toggleExpand = (id: string) => {
    setExpandedFilterIds(prev => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const handleAddPair = useCallback(
  (pair: string | StockObject[]) => {
    if (!pair) return;

    if (typeof pair === "string") {
      // Single crypto
      const normalizedPair: CryptoPair = { symbol: pair.toUpperCase(), category: "crypto" };
      setSelectedPairs((prev) => {
        const exists = prev.some(
          (p) => "symbol" in p && p.symbol === normalizedPair.symbol
        );
        return exists ? prev : [...prev, normalizedPair];
      });
    } else {
      // Array of StockObject
      setSelectedPairs((prev) => {
        // Filter out stocks already in selectedPairs
        const newStocks = pair.filter(
          (stock) =>
            !prev.some(
              (p) =>
                "security_id" in p &&
                p.security_id === stock.security_id
            )
        );

        const updated = [...prev, ...newStocks];
        handleSavePairs(updated);
        return updated
      });
    }
    setPairSearch("");
  },
  []
);

  const handleRemovePair = useCallback(
    (pair: CryptoPair | StockObject) => {
      setSelectedPairs((prev) =>
        prev.filter((p) => {
          // Crypto removal
          if ("symbol" in pair && "symbol" in p) {
            return p.symbol !== pair.symbol;
          }

          // Stock removal
          if ("underlying_symbol" in pair && "underlying_symbol" in p) {
            return p.underlying_symbol !== pair.underlying_symbol;
          }

          return true; // fallback if mismatch
        })
      );
    },
    []
  );

  const handleSavePairs = useCallback((pairs: (CryptoPair | StockObject)[]) => {
    if (pairs.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one pair",
        variant: "destructive",
      });
      return;
    }

    createLeader(pairs)
  }, [createLeader]);


  // ==================== RENDER FUNCTIONS ====================
  const renderLeaderItem = useCallback(
    (leader: CryptoPair | StockObject, index: number) => {
      const isCrypto = "symbol" in leader;

      return (
        <TableRow
          key={leader._id ?? index}
          className="hover:bg-gray-50/50 transition-colors"
        >
          <TableCell className="font-semibold text-slate-900">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-gray-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                {(isCrypto
                  ? leader.symbol.slice(0, 2)
                  : leader.symbol_name.slice(0, 2)
                ).toUpperCase()}
              </div>

              {isCrypto ? leader.symbol : leader.symbol_name}
            </div>
          </TableCell>

          <TableCell>
            <Badge variant="outline" className="font-medium">
              {isCrypto ? "Crypto Pair" : leader.instrument_type}
            </Badge>
          </TableCell>

          <TableCell className="text-right">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteLeader(leader._id!)}
                    disabled={isDeletingLeader}
                    className="hover:bg-red-50 hover:text-red-600"
                  >
                    {isDeletingLeader ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete leader</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        </TableRow>
      );
    },
    [deleteLeader, isDeletingLeader]
  );

  const renderStrategyItem = useCallback((strategy: FilterApiSchema) => {
    const isExpanded = expandedFilterIds.has(strategy.id!);
    return (
      <div
        key={strategy.id}
        className="p-4 border-2 border-slate-200 rounded-xl hover:border-indigo-300 hover:shadow-lg transition-all duration-200 bg-white group"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge
                variant={strategy.isActive ? "default" : "secondary"}
                className={strategy.isActive ? "bg-green-500" : ""}
              >
                {strategy.isActive ? "Active" : "Inactive"}
              </Badge>
            </div>
            <h4 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">
              {strategy.function_name}
            </h4>
            <p className="text-xs text-slate-500 mt-1">
              {strategy.variables.length} variables • {strategy.category}
            </p>
          </div>
        </div>

        {isExpanded && (
                  <div className="mb-3 rounded-lg border border-slate-200 overflow-hidden">
                    <div className="bg-slate-900 px-3 py-1.5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Code className="h-3 w-3 text-slate-400" />
                        <span className="text-xs font-mono text-slate-400">filter_code</span>
                      </div>
                    </div>
                    <div className="bg-slate-50 p-3 max-h-60 overflow-auto">
                      <pre className="text-xs font-mono text-slate-800 whitespace-pre-wrap">
                        {strategy.function_code!}
                      </pre>
                    </div>
                  </div>
                )}

        <div className="flex gap-1 pt-3 border-t border-slate-100">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleExpand(strategy.id!)}
                  className="flex-1 hover:bg-gray-50 hover:text-gray-600"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isExpanded ? "Hide code" : "View code"}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditStrategy(strategy)}
                  className="flex-1 hover:bg-amber-50 hover:text-amber-600"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit strategy</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => strategy.id && deleteStrategy(strategy.id)}
                  disabled={isDeletingStrategy}
                  className="flex-1 hover:bg-red-50 hover:text-red-600"
                >
                  {isDeletingStrategy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete strategy</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    );
  }, [handleEditStrategy, toggleExpand, deleteStrategy, isDeletingStrategy]);

  // ============================ UI =================================

  return (
    <div className="min-h-screen ">

      <div className="px-2 mx-auto py-8 space-y-8">
        {/* Leaders Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-1 bg-gray-600 rounded-full"></div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Active Leaders</h2>
                <p className="text-slate-600">Manage your tracked market leaders</p>
              </div>
            </div>
            <Button onClick={() => setPairModalOpen(true)} className="gap-2 bg-white hover:bg-gray-300 border border-slate-200 ">
              <Plus className="h-4 w-4" />
              Add New Leader
            </Button>
          </div>

          <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6">
              {isLoadingLeaders ? (
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : leadersError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error loading leaders</AlertTitle>
                  <AlertDescription>
                    Failed to load leaders. Please try again.
                  </AlertDescription>
                </Alert>
              ) : leaders && leaders.length > 0 ? (
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableHead className="font-semibold text-slate-900">Symbol</TableHead>
                        <TableHead className="font-semibold text-slate-900">Type</TableHead>
                        <TableHead className="font-semibold text-slate-900 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leaders.map((leader, index) => renderLeaderItem(leader, index))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <EmptyState
                  icon={<TrendingUp className="h-12 w-12 text-slate-400" />}
                  title="No leaders configured"
                  description="Add your first market leader to begin tracking"
                />
              )}
            </CardContent>
          </Card>
        </div>

        <Separator className="my-8" />

        {/* Strategies Section */}
        <div className="space-y-4" id="strategy-editor">
          <div className="flex items-center gap-3">
            <div className="h-10 w-1 bg-indigo-600 rounded-full"></div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Leader Selection Strategies</h2>
              <p className="text-slate-600">Create and manage intelligent selection algorithms</p>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Strategy Editor */}
            <Card className="xl:col-span-2 border-0 shadow-xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="border-b bg-gradient-to-r from-indigo-50 to-gray-50">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Sparkles className="h-5 w-5 text-indigo-600" />
                      {editingStrategy ? "Edit Strategy" : "Create New Strategy"}
                    </CardTitle>
                    <CardDescription>
                      Write Python code to define your leader selection criteria
                    </CardDescription>
                  </div>
                  {editingStrategy && (
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200">
                      Editing Mode
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="strategy-name" className="text-sm font-semibold text-slate-700">
                      Strategy Name
                    </Label>
                    <h1>{strategyName?strategyName:"None"}</h1>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700">Strategy Status</Label>
                    <div className="flex items-center space-x-3 h-10 px-3 border border-slate-300 rounded-lg bg-slate-50">
                      <Switch
                        checked={isActive}
                        onCheckedChange={setIsActive}
                        id="strategy-active"
                      />
                      <Label htmlFor="strategy-active" className="text-sm font-medium cursor-pointer">
                        {isActive ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <div className="h-2 w-2 rounded-full bg-green-600 animate-pulse"></div>
                            Active
                          </span>
                        ) : (
                          <span className="text-slate-500 flex items-center gap-1">
                            <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                            Inactive
                          </span>
                        )}
                      </Label>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Python Strategy Code</Label>
                  <div className="rounded-xl border-2 border-slate-200 overflow-hidden shadow-inner">
                    <div className="bg-slate-900 px-4 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Code className="h-4 w-4 text-slate-400" />
                        <span className="text-xs font-mono text-slate-400">strategy.py</span>
                      </div>
                      <Badge variant="outline" className="text-xs border-slate-700 text-slate-400">
                        Python 3.x
                      </Badge>
                    </div>
                    <Editor
                      height="400px"
                      language="python"
                      theme="vs-dark"
                      value={codeInput}
                      onChange={(value) => setCodeInput(value || "")}
                      onMount={(editor) => (editorRef.current = editor)}
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        scrollBeyondLastLine: false,
                        wordWrap: "on",
                      }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-2">
                    <AlertCircle className="h-3 w-3" />
                    Use data.get() to access available variables like volume, price, change, etc.
                  </p>
                </div>

                <div className="flex justify-between pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={resetEditor}
                    disabled={isCreatingStrategy || isUpdatingStrategy}
                    className="gap-2"
                  >
                    <X className="h-4 w-4" />
                    Clear Editor
                  </Button>
                  <Button
                    onClick={editingStrategy ? handleUpdateStrategy : handleAddStrategy}
                    disabled={isCreatingStrategy || isUpdatingStrategy}
                    className="gap-2 bg-gradient-to-r text-white from-gray-800 to-gray-600 hover:from-gray-700 hover:to-gray-800 "
                  >
                    {isCreatingStrategy || isUpdatingStrategy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {editingStrategy ? "Update Strategy" : "Create Strategy"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Strategies List */}
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-slate-100">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Settings className="h-5 w-5 text-slate-600" />
                  Your Strategies
                </CardTitle>
                <CardDescription>
                  {strategies?.length || 0} strategy{strategies?.length !== 1 ? 'ies' : ''} configured
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4">
                {isLoadingStrategies ? (
                  <div className="space-y-3">
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-20 w-full" />
                  </div>
                ) : strategiesError ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error loading strategies</AlertTitle>
                    <AlertDescription>
                      Failed to load strategies. Please try again.
                    </AlertDescription>
                  </Alert>
                ) : strategies && strategies.length > 0 ? (
                  <ScrollArea className="h-[520px] pr-4">
                    <div className="space-y-3">
                      {strategies.map(renderStrategyItem)}
                    </div>
                  </ScrollArea>
                ) : (
                  <EmptyState
                    icon={<Code className="h-10 w-10 text-slate-400" />}
                    title="No strategies yet"
                    description="Create your first strategy above"
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Leader Modal */}
      <Dialog open={pairModalOpen} onOpenChange={setPairModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl">Add New Leader</DialogTitle>
            <DialogDescription>
              Add a new {category === "crypto" ? "cryptocurrency pair" : "stock"} to your leaders list
            </DialogDescription>
          </DialogHeader>

          {category === "crypto" ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="pair-search" className="font-semibold">
                  Cryptocurrency Pair
                </Label>
                <Input
                  id="pair-search"
                  placeholder="e.g., BTCUSDT"
                  value={pairSearch}
                  onChange={(e) => setPairSearch(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && pairSearch.trim()) {
                      handleAddPair(pairSearch);
                    }
                  }}
                  className="text-lg"
                />

                <Button
                  onClick={() => handleAddPair(pairSearch)}
                  disabled={!pairSearch.trim() || isCreatingLeader}
                  className="w-full mt-2 bg-green-600 hover:bg-green-700 text-white"
                >
                  {isCreatingLeader ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Add Pair
                </Button>
              </div>

              {selectedPairs.length > 0 && (
                <div className="space-y-2">
                  <Label className="font-semibold">Selected Items ({selectedPairs.length})</Label>
                  <div className="flex flex-wrap gap-2 p-4 border-2 border-dashed rounded-xl bg-slate-50">
                    {selectedPairs.map((pair, index) => (
                      <Badge key={index} variant="secondary" className="px-3 py-1.5 text-sm">
                        {"symbol" in pair ? pair.symbol : pair.symbol_name}
                        <button
                          onClick={() => handleRemovePair(pair)}
                          className="ml-2 hover:text-destructive"
                          disabled={isCreatingLeader}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <CustomModelAddStocks
              onAddStocks={handleAddPair}
              onClose={() => setPairModalOpen(false)}
              show={pairModalOpen}
            />
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPairModalOpen(false)}>
              Cancel
            </Button>

            <Button
              onClick={() => handleSavePairs(selectedPairs)}
              disabled={selectedPairs.length === 0 || isCreatingLeader}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
            >
              {isCreatingLeader ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Leaders
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper component for empty states
interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({ icon, title, description }) => (
  <div className="flex flex-col items-center justify-center p-12 text-center">
    <div className="p-4 rounded-2xl bg-slate-100 mb-4">
      {icon}
    </div>
    <h3 className="font-semibold text-lg mb-2 text-slate-900">{title}</h3>
    <p className="text-slate-500 text-sm max-w-sm">{description}</p>
  </div>
);